import React, { useEffect, useState, useContext, useCallback } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";
import PaginatedTable from "../components/Table/PaginatedTable";
import { toast, ToastContainer } from "react-toastify";
import DeleteModal from "../components/common/DeleteModel";

const TestCaseView = () => {
  const APIServices = new APIServicenew(
    process.env.REACT_APP_OTHER_API_URL
  );
  const { accessToken, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const { folderId } = useParams();

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  /* ================= STATE ================= */
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [folderName, setFolderName] = useState("");
  const [isEditingFolder, setIsEditingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);


  /* ================= API CALL ================= */
  const fetchTestCases = useCallback(async () => {
    setLoading(true);
    try {
      const response = await APIServices.get(
        `/test-execution/folders/test-cases?folder_id=${folderId}&page=${page}&limit=${limit}`,
        headers
      );

      if (response?.status === 200) {
        setTestCases(response.data?.testCases || []);
        setFolderName(response.data?.folder_name || "");
        setNewFolderName(response.data?.folder_name || "");
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching test cases:", error);
    } finally {
      setLoading(false);
    }
  }, [folderId, page, limit]);

  useEffect(() => {
    fetchTestCases();
  }, [fetchTestCases]);

  /* ================= UPDATE FOLDER ================= */
  const updateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Folder name cannot be empty");
      return;
    }

    try {
      const response = await APIServices.put(
        `test-execution/folders/${folderId}`,
        { folder_name: newFolderName },
        headers
      );

      if (response?.status === 200) {
        toast.success("Folder updated successfully!");
        setFolderName(newFolderName);
        setIsEditingFolder(false);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        toast.error("Failed to update folder");
      }
    } catch (error) {
      console.error("Error updating folder:", error);
      toast.error("Failed to update folder");
    }
  };

  /* ================= DELETE TEST CASE ================= */
  const handleDelete = async () => {
    if (!selectedCaseId) return;
    setShowDeleteModal(false);

    try {
      const response = await APIServices.delete(
        `/admin/test-cases/${selectedCaseId}`,{},
        headers
      );

      if (response?.status === 200) {
        toast.success("Test case deleted successfully!");
        fetchTestCases();
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        toast.error("Failed to delete test case");
      }
    } catch (error) {
      console.error("Error deleting test case:", error);
      toast.error("Failed to delete test case");
    }
  };


  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { dataField: "name", text: "Test Case Name" },
    { dataField: "modality", text: "Modality" },
    { dataField: "section", text: "Section" },
    { dataField: "bodyPart", text: "Body Part" },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => (
        <button
          className="btn btn-danger btn-sm"
          onClick={() => {
            setSelectedCaseId(row.id);
            setShowDeleteModal(true);
          }}
        >
          <i className="fa-solid fa-trash"></i>
        </button>
      ),
    },
  ];

  /* ================= JSX ================= */
  return (
    <div className="all_content test_case_view">
      <Container fluid>
        {/* Header */}
        <div className="head-section d-flex justify-content-between align-items-center">
          <h1 className="font_16 font-600 clr_blue"> Test Cases </h1>

          <button className="btn clr_sky" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>

        {/* Folder Name with Edit */}
        <div className="d-flex align-items-center gap-2 ml-2">
          {isEditingFolder ? (
            <>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="form-control h_40"
                style={{ maxWidth: "250px" }}
              />
              <button className="btn btn-success btn-md" onClick={updateFolder}>
                Update
              </button>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => {
                  setIsEditingFolder(false);
                  setNewFolderName(folderName);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <div className="case_edit_button">
              <h1 className="font_16 font-600 text-white">{folderName}</h1>
              <button
                className="btn p-0"
                onClick={() => setIsEditingFolder(true)}
                title="Edit folder name"
              >
                <i className="fas fa-pencil-alt text-white"></i>
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="comman_table mt-4">
          <PaginatedTable
            data={testCases}
            columns={columns}
            currentPage={page}
            onPageChange={setPage}
            limit={limit}
            setLimit={setLimit}
            loading={loading}
            showPagination={false}
            emptyMessage="No test cases found"
          />
        </div>
      </Container>
      <DeleteModal
        showdt={showDeleteModal}
        handleCloseDt={() => setShowDeleteModal(false)}
        handledelete={handleDelete}
      />
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default TestCaseView;
